/**
 * Auto-assign new items to in-progress count sessions.
 *
 * Rules (from task spec):
 *  - Inventory / EOM counts (order_calculations_enabled != true):
 *      Add the item if the session already covers the item's category
 *      (i.e. at least one existing count_item in the session shares the category),
 *      OR if the session covers all categories (no category restriction inferred).
 *  - Ordering counts (order_calculations_enabled == true):
 *      Add the item if the session's vendor matches the new item's vendor.
 *  - Completed sessions are NEVER modified.
 *
 * Returns an array of { session, countItems } objects describing which sessions
 * the item will be added to (pre-checked by default; UI can deselect).
 */

import { SUPABASE_URL, SUPABASE_KEY } from '../config/database';

function normalize(value) {
  return String(value ?? '').toLowerCase().trim();
}

async function getHeaders() {
  try {
    if (window.supabase?.auth) {
      const { data } = await window.supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (token) {
        return {
          Authorization: `Bearer ${token}`,
          apikey: SUPABASE_KEY,
          'Content-Type': 'application/json',
        };
      }
    }
  } catch (_) {}
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith('sb-') || !key.endsWith('-auth-token')) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (parsed?.access_token) {
        return {
          Authorization: `Bearer ${parsed.access_token}`,
          apikey: SUPABASE_KEY,
          'Content-Type': 'application/json',
        };
      }
    } catch (_) {}
  }
  return { Authorization: `Bearer ${SUPABASE_KEY}`, apikey: SUPABASE_KEY, 'Content-Type': 'application/json' };
}

async function restGet(path, params = {}) {
  const headers = await getHeaders();
  const qs = Object.entries(params)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  const url = `${SUPABASE_URL}/rest/v1/${path}${qs ? '?' + qs : ''}`;
  const resp = await fetch(url, { headers: { ...headers, Accept: 'application/json' } });
  if (!resp.ok) return [];
  return resp.json().catch(() => []);
}

/**
 * Find all in-progress count sessions that should receive the new item,
 * and describe what to insert.
 *
 * @param {object} params
 * @param {number}  params.storeId
 * @param {string}  params.itemCategory
 * @param {string}  params.itemVendor
 * @param {number}  params.inventoryItemId  – the newly inserted item's id
 * @param {object}  params.itemSnapshot     – snapshot object for count_items
 *
 * @returns {Promise<Array<{ session: object, alreadyPresent: boolean }>>}
 */
export async function findCountTargetsForNewItem({ storeId, itemCategory, itemVendor, inventoryItemId, itemSnapshot }) {
  if (!storeId) return [];

  const sessions = await restGet('count_sessions', {
    store_id: `eq.${storeId}`,
    status: 'eq.in_progress',
    is_archived: 'not.is.true',
    is_trashed: 'not.is.true',
    select: 'id,name,status,order_calculations_enabled,vendor_id,vendors(display_name)',
  });

  if (!sessions.length) return [];

  const targets = [];
  const normCategory = normalize(itemCategory);
  const normVendor = normalize(itemVendor);

  for (const session of sessions) {
    const isOrdering = session.order_calculations_enabled === true;

    // Only fetch count_items for inventory sessions (category matching).
    // Ordering sessions use the session's direct vendor_id/vendor for matching.
    let items = [];
    if (!isOrdering) {
      items = await restGet('count_items', {
        count_session_id: `eq.${session.id}`,
        select: 'id,inventory_item_id,item_snapshot',
        limit: '500',
      });
    } else if (inventoryItemId != null) {
      // For ordering sessions, still need items to check alreadyPresent
      items = await restGet('count_items', {
        count_session_id: `eq.${session.id}`,
        inventory_item_id: `eq.${inventoryItemId}`,
        select: 'id,inventory_item_id',
        limit: '1',
      });
    }

    const alreadyPresent = inventoryItemId != null && items.some(
      (ci) => String(ci.inventory_item_id) === String(inventoryItemId)
    );

    let shouldInclude = false;

    if (isOrdering) {
      // Match on the session's direct vendor, not inferred from count_items.
      // The session's vendor is available via vendor_id + vendors join, or
      // via vendor_name_snapshot stored on the session.
      if (normVendor) {
        const sessionVendorName = normalize(
          session.vendors?.display_name ||
          session.vendor_name ||
          session.vendor_name_snapshot ||
          ''
        );
        shouldInclude = sessionVendorName && sessionVendorName === normVendor;
      }
    } else {
      if (!normCategory) {
        shouldInclude = false;
      } else {
        const sessionCategories = new Set(
          items.map((ci) => normalize(ci.item_snapshot?.category || ''))
        );
        const coversAll = sessionCategories.size === 0;
        shouldInclude = coversAll || sessionCategories.has(normCategory);
      }
    }

    if (shouldInclude) {
      targets.push({ session, alreadyPresent });
    }
  }

  return targets;
}

/**
 * Insert a count_items row for each selected target session.
 *
 * @param {object} params
 * @param {number}  params.storeId
 * @param {number}  params.inventoryItemId
 * @param {object}  params.itemSnapshot
 * @param {Array<object>} params.selectedSessions – session objects (id, name, …)
 */
export async function assignItemToCountSessions({ storeId, inventoryItemId, itemSnapshot, selectedSessions }) {
  if (!storeId || !inventoryItemId || !selectedSessions?.length) return { inserted: 0, errors: [] };

  const headers = await getHeaders();
  const errors = [];
  let inserted = 0;

  for (const session of selectedSessions) {
    const payload = {
      count_session_id: session.id,
      inventory_item_id: inventoryItemId,
      store_id: storeId,
      item_snapshot: itemSnapshot || {},
      location: itemSnapshot?.location || null,
    };

    try {
      const resp = await fetch(`${SUPABASE_URL}/rest/v1/count_items`, {
        method: 'POST',
        headers: { ...headers, Prefer: 'return=minimal' },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        inserted++;
      } else {
        const txt = await resp.text().catch(() => '');
        if (!txt.includes('duplicate') && !txt.includes('23505')) {
          errors.push(`Session "${session.name}": ${resp.status} ${txt}`);
        }
      }
    } catch (err) {
      errors.push(`Session "${session.name}": ${err?.message}`);
    }
  }

  return { inserted, errors };
}
